import { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularLoader,
  CloseIcon,
  Control,
  EmptyState,
  FilterIcon,
  Input,
  Label,
  LockIcon,
  Modal,
  ModalContent,
  Pagination,
  RefreshIcon,
  SearchIcon,
  Select,
  SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useDocumentTitle,
} from '@forge/common';
import type { AuditLogFilters } from '@/api/audit';
import { useAuditLogs } from '@/hooks/useAuditLogs';
import { useAuditFacets } from '@/hooks/useAuditFacets';

/**
 * Converts a raw backend token (e.g. "keycloak_group_member_added" or
 * "share_created") into a human-readable label. Using one humanizer for both
 * the filter dropdowns and the table badges guarantees they always line up.
 */
const humanizeToken = (value: string): string =>
  value
    .split(/[._]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

type FilterField = 'action' | 'entityType' | 'actor' | 'dateFrom' | 'dateTo';

type ActiveFilterChip = {
  key: FilterField;
  label: string;
  value: string;
};

const normalizeOptionalValue = (value: string | undefined): string | undefined => {
  const trimmedValue = value?.trim();
  return trimmedValue ? trimmedValue : undefined;
};

const sanitizeFilters = (filters: AuditLogFilters): AuditLogFilters => ({
  action: normalizeOptionalValue(filters.action),
  entityType: normalizeOptionalValue(filters.entityType),
  actor: normalizeOptionalValue(filters.actor),
  dateFrom: normalizeOptionalValue(filters.dateFrom),
  dateTo: normalizeOptionalValue(filters.dateTo),
});

const getActionLabel = (action: string): string => {
  return humanizeToken(action);
};

const getEntityLabel = (entityType: string): string => {
  return humanizeToken(entityType);
};

const getActionBadgeColor = (
  action: string,
): 'red' | 'orange' | 'blue' | 'green' | 'gray' | 'yellow' => {
  if (
    action.includes('delete') ||
    action.includes('remove') ||
    action.includes('revoke')
  ) {
    return 'red';
  }

  if (
    action.includes('update') ||
    action.includes('assign') ||
    action.includes('mfa.verify')
  ) {
    return 'yellow';
  }

  if (
    action.includes('download') ||
    action.includes('login') ||
    action.includes('logout')
  ) {
    return 'blue';
  }

  return 'green';
};

const getEntityBadgeColor = (
  entityType: string,
): 'red' | 'orange' | 'blue' | 'green' | 'gray' | 'yellow' => {
  if (entityType.includes('auth')) {
    return 'red';
  }
  if (entityType.includes('user')) {
    return 'green';
  }
  if (entityType.includes('group')) {
    return 'orange';
  }
  if (entityType.includes('client')) {
    return 'yellow';
  }
  if (entityType.includes('share')) {
    return 'gray';
  }
  if (entityType.includes('file')) {
    return 'blue';
  }
  return 'gray';
};

const formatTimestamp = (timestamp: string): string => {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(timestamp));
};

const formatFilterDate = (date: string): string => {
  const parsedDate = new Date(`${date}T00:00:00`);

  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
  }).format(parsedDate);
};

const clearFilterField = (
  currentFilters: AuditLogFilters,
  field: FilterField,
): AuditLogFilters => {
  const nextFilters: AuditLogFilters = { ...currentFilters };
  nextFilters[field] = undefined;
  return sanitizeFilters(nextFilters);
};

const truncateEntityId = (entityId: string | undefined): string => {
  if (!entityId) {
    return '—';
  }

  if (entityId.length <= 16) {
    return entityId;
  }

  return `${entityId.slice(0, 8)}…${entityId.slice(-4)}`;
};

const AuditLogsPage = () => {
  useDocumentTitle('Audit Logs — OneRing Portal');

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [filterForm, setFilterForm] = useState<AuditLogFilters>({});
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | undefined>(undefined);

  const { logs, total, isLoading: loading, error, isUsingMockData: usingMockData, reload } = useAuditLogs({
    filters,
    page,
    limit,
  });

  const { actions: actionFacets, entityTypes: entityFacets } = useAuditFacets();

  const actionOptions = useMemo(
    () => [
      { label: 'All Actions', value: '' },
      ...actionFacets.map((value) => ({ label: humanizeToken(value), value })),
    ],
    [actionFacets],
  );

  const entityOptions = useMemo(
    () => [
      { label: 'All Entities', value: '' },
      ...entityFacets.map((value) => ({ label: humanizeToken(value), value })),
    ],
    [entityFacets],
  );

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(total / limit));
  }, [limit, total]);

  const visibleCount = useMemo(() => logs.length, [logs.length]);

  const activeFilters = useMemo<ActiveFilterChip[]>(() => {
    const nextFilters: ActiveFilterChip[] = [];

    if (filters.action) {
      nextFilters.push({
        key: 'action',
        label: 'Action',
        value: getActionLabel(filters.action),
      });
    }

    if (filters.entityType) {
      nextFilters.push({
        key: 'entityType',
        label: 'Entity',
        value: getEntityLabel(filters.entityType),
      });
    }

    if (filters.actor) {
      nextFilters.push({
        key: 'actor',
        label: 'Actor',
        value: filters.actor,
      });
    }

    if (filters.dateFrom) {
      nextFilters.push({
        key: 'dateFrom',
        label: 'From',
        value: formatFilterDate(filters.dateFrom),
      });
    }

    if (filters.dateTo) {
      nextFilters.push({
        key: 'dateTo',
        label: 'To',
        value: formatFilterDate(filters.dateTo),
      });
    }

    return nextFilters;
  }, [filters]);

  const activeFilterCount = activeFilters.length;

  const handleSearch = useCallback(() => {
    const nextFilters = sanitizeFilters(filterForm);
    setExpandedRow(undefined);
    setPage(1);
    setFilterForm(nextFilters);
    setFilters(nextFilters);
  }, [filterForm]);

  const handleReset = useCallback(() => {
    setExpandedRow(undefined);
    setPage(1);
    setFilterForm({});
    setFilters({});
  }, []);

  const handleOpenFilterModal = useCallback(() => {
    setFilterForm(filters);
    setFilterModalOpen(true);
  }, [filters]);

  const handleCloseFilterModal = useCallback(() => {
    setFilterForm(filters);
    setFilterModalOpen(false);
  }, [filters]);

  const handleApplyFilters = useCallback(() => {
    handleSearch();
    setFilterModalOpen(false);
  }, [handleSearch]);

  const handleClearFilters = useCallback(() => {
    handleReset();
    setFilterModalOpen(false);
  }, [handleReset]);

  const handleRefresh = useCallback(() => {
    void reload();
  }, [reload]);

  const handleRemoveFilter = useCallback((field: FilterField) => {
    setExpandedRow(undefined);
    setPage(1);
    setFilters((currentValue) => clearFilterField(currentValue, field));
    setFilterForm((currentValue) => clearFilterField(currentValue, field));
  }, []);

  const handleRowToggle = useCallback((logId: string) => {
    setExpandedRow((currentValue) =>
      currentValue === logId ? undefined : logId,
    );
  }, []);

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-1">Audit Logs</h2>
      <p className="text-sm text-color-tertiary mb-4">
        View and filter platform activity logs
      </p>

      {usingMockData && (
        <div className="mb-4">
          <Banner
            variant="inline"
            type="info"
            title=""
            hideIcon
            canClose={false}
            description="Audit logs are displaying mock data for demonstration purposes. Changes will not be persisted and will reset on page reload."
          />
        </div>
      )}

      {filterModalOpen ? (
        <Modal
          title="Filter Audit Logs"
          onModalClose={handleCloseFilterModal}
          onBackdropClick={handleCloseFilterModal}
          footerLeft={
            <Button variant="tertiary" onClick={handleClearFilters}>
              Clear All
            </Button>
          }
          footerRight={
            <Button variant="primary" onClick={handleApplyFilters}>
              Apply Filters
            </Button>
          }
        >
          <ModalContent padded>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Control>
                <Label htmlFor="audit-action-filter">Action Type</Label>
                <Select
                  id="audit-action-filter"
                  value={filterForm.action ?? ''}
                  onChange={(event) =>
                    setFilterForm((currentValue) => ({
                      ...currentValue,
                      action: event.target.value || undefined,
                    }))
                  }
                >
                  {actionOptions.map((option) => (
                    <SelectOption
                      key={option.value || 'all-actions'}
                      value={option.value}
                      label={option.label}
                    >
                      {option.label}
                    </SelectOption>
                  ))}
                </Select>
              </Control>

              <Control>
                <Label htmlFor="audit-entity-filter">Entity Type</Label>
                <Select
                  id="audit-entity-filter"
                  value={filterForm.entityType ?? ''}
                  onChange={(event) =>
                    setFilterForm((currentValue) => ({
                      ...currentValue,
                      entityType: event.target.value || undefined,
                    }))
                  }
                >
                  {entityOptions.map((option) => (
                    <SelectOption
                      key={option.value || 'all-entities'}
                      value={option.value}
                      label={option.label}
                    >
                      {option.label}
                    </SelectOption>
                  ))}
                </Select>
              </Control>

              <Control>
                <Label htmlFor="audit-actor-filter">Actor</Label>
                <Input
                  id="audit-actor-filter"
                  placeholder="Search by actor"
                  prefix={<SearchIcon />}
                  value={filterForm.actor ?? ''}
                  onChange={(event) =>
                    setFilterForm((currentValue) => ({
                      ...currentValue,
                      actor: event.target.value,
                    }))
                  }
                />
              </Control>

                <div className="flex gap-4">
                  <Control className="flex-1">
                    <Label htmlFor="audit-date-from">Date From</Label>
                    <Input
                      id="audit-date-from"
                      type="date"
                      value={filterForm.dateFrom ?? ''}
                      onChange={(event) =>
                        setFilterForm((currentValue) => ({
                          ...currentValue,
                          dateFrom: event.target.value || undefined,
                        }))
                      }
                    />
                  </Control>

                  <Control className="flex-1">
                    <Label htmlFor="audit-date-to">Date To</Label>
                    <Input
                      id="audit-date-to"
                      type="date"
                      value={filterForm.dateTo ?? ''}
                      onChange={(event) =>
                        setFilterForm((currentValue) => ({
                          ...currentValue,
                          dateTo: event.target.value || undefined,
                        }))
                      }
                    />
                  </Control>
                </div>
              </div>
            </ModalContent>
        </Modal>
      ) : undefined}

      {error && (
        <div className="my-4">
          <Banner
            variant="inline"
            type="error"
            title="Failed to load audit logs"
            description={error}
            canClose={false}
          />
        </div>
      )}

      <Card>
        <CardHeader
          title="Activity Log"
          subtitle={`Showing ${visibleCount} of ${total} ${total === 1 ? 'entry' : 'entries'}`}
          slotRight={
            <div className="flex items-center gap-3">
              <Button
                variant="tertiary"
                icon={<FilterIcon />}
                onClick={handleOpenFilterModal}
              >
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
              </Button>
              {activeFilterCount > 0 ? (
                <Button variant="tertiary" onClick={handleReset}>
                  Reset
                </Button>
              ) : undefined}
              <Button
                variant="tertiary"
                icon={<RefreshIcon />}
                disabled={loading}
                onClick={handleRefresh}
              >
                Refresh
              </Button>
            </div>
          }
        />
        <CardContent>
          <div className="flex flex-col gap-4">
            {activeFilterCount > 0 ? (
              <div className="flex flex-wrap gap-2">
                {activeFilters.map((filter) => (
                  <span
                    key={filter.key}
                    className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-color-secondary dark:border-gray-800 dark:bg-gray-950/50"
                  >
                    <span className="font-medium">{filter.label}:</span>
                    <span>{filter.value}</span>
                    <button
                      type="button"
                      className="text-color-tertiary transition-colors hover:text-color-secondary"
                      aria-label={`Remove ${filter.label} filter`}
                      onClick={() => handleRemoveFilter(filter.key)}
                    >
                      <CloseIcon className="h-3.5 w-3.5" />
                    </button>
                  </span>
                ))}
              </div>
            ) : undefined}

            {loading ? (
              <div className="flex min-h-[320px] flex-col items-center justify-center gap-3">
                <CircularLoader size="large" />
                <p className="text-sm text-color-tertiary">Loading audit logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="py-8">
                <EmptyState
                  icon={<LockIcon />}
                  description="No audit logs found"
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Timestamp</TableCell>
                      <TableCell>Action</TableCell>
                      <TableCell>Entity Type</TableCell>
                      <TableCell>Entity ID</TableCell>
                      <TableCell>Actor</TableCell>
                      <TableCell>IP Address</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.flatMap((log) => {
                      const isExpanded = expandedRow === log.id;

                      return [
                        <TableRow
                          key={log.id}
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          className="cursor-pointer align-top transition-colors hover:bg-gray-50 dark:hover:bg-gray-900/40"
                          onClick={() => handleRowToggle(log.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleRowToggle(log.id);
                            }
                          }}
                        >
                          <TableCell>{formatTimestamp(log.createdAt)}</TableCell>
                          <TableCell>
                            <Badge color={getActionBadgeColor(log.action)}>
                              {getActionLabel(log.action)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge color={getEntityBadgeColor(log.entityType)}>
                              {getEntityLabel(log.entityType)}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-mono text-xs text-color-secondary">
                              {truncateEntityId(log.entityId)}
                            </span>
                          </TableCell>
                          <TableCell>{log.actor}</TableCell>
                          <TableCell>{log.ipAddress ?? '—'}</TableCell>
                        </TableRow>,
                        ...(isExpanded
                          ? [
                              <TableRow key={`${log.id}-details`}>
                                <TableCell colSpan={6}>
                                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-950/50">
                                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-color-tertiary">
                                      Event Details
                                    </p>
                                    <pre className="overflow-x-auto whitespace-pre-wrap break-words font-mono text-xs text-color-secondary">
                                      {log.details
                                        ? JSON.stringify(log.details, undefined, 2)
                                        : 'No additional details available.'}
                                    </pre>
                                  </div>
                                </TableCell>
                              </TableRow>,
                            ]
                          : []),
                      ];
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            <Pagination
              currentPage={page}
              totalItems={total}
              totalPages={totalPages}
              pageSize={limit}
              pageSizeOptions={[25, 50, 100]}
              showPageInfo
              showPageSizeList
              onPageChange={(newPage) => {
                setExpandedRow(undefined);
                setPage(newPage);
              }}
              onPageSizeChange={(newSize) => {
                setExpandedRow(undefined);
                setPage(1);
                setLimit(newSize);
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogsPage;
